// Page-view tracker — feeds the admin "Member activity" timeline.
//
// The SPA has no server-visible navigation (GETs never audit), so the router
// root batches each pathname change and posts it to
// POST /api/v1/me/activity/page-views, where it becomes a `ui.page.viewed`
// audit row attributed to the signed-in user. Real-engine + token only; the
// maket (MSW) mode and public pages never send anything.
//
// Pure queue core (tested) + a thin React hook (root-shell).

import { useEffect, useRef } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { apiUrl, USE_REAL_API } from './api'
import { useAuthStore } from '../store/auth'

export type PageView = { path: string; label?: string; occurred_at: string }

// Never tracked: anonymous / onboarding surfaces and the bare root.
const IGNORED_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/accept',
  '/apply',
  '/onboarding',
  '/start-company',
  '/start-a-company',
]

export function isIgnoredPath(path: string): boolean {
  if (path === '/' || path === '') return true
  return IGNORED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))
}

const ID_LIKE = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+|[a-z]{2,4}_[a-z0-9]+)$/i

// "/seller/orders/1dd57c81-…" → "Seller › Orders › detail". Used both as the
// stored label and as the admin-side fallback for rows without one.
export function routeLabel(path: string): string {
  const segments = path.split('?')[0].split('/').filter(Boolean)
  if (segments.length === 0) return 'Home'
  return segments
    .map((seg) =>
      ID_LIKE.test(seg)
        ? 'detail'
        : seg
            .replace(/[-_]+/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()),
    )
    .join(' › ')
}

export type PageViewQueue = {
  push: (path: string, now?: Date) => void
  flush: () => Promise<void>
  size: () => number
}

type QueueOptions = {
  send: (events: PageView[]) => Promise<void> | void
  flushMs?: number
  maxBatch?: number
  schedule?: (fn: () => void, ms: number) => unknown
}

export function createPageViewQueue(opts: QueueOptions): PageViewQueue {
  const flushMs = opts.flushMs ?? 5000
  const maxBatch = opts.maxBatch ?? 20
  const schedule = opts.schedule ?? ((fn, ms) => setTimeout(fn, ms))
  let buffer: PageView[] = []
  let lastPath: string | null = null
  let timerArmed = false

  async function flush(): Promise<void> {
    timerArmed = false
    if (buffer.length === 0) return
    const batch = buffer.slice(0, 50)
    buffer = buffer.slice(50)
    try {
      await opts.send(batch)
    } catch {
      // Best-effort telemetry — never retry into a loop, never surface.
    }
  }

  return {
    push(path, now = new Date()) {
      const clean = path.split('?')[0]
      if (isIgnoredPath(clean)) return
      if (clean === lastPath) return
      lastPath = clean
      buffer.push({ path: clean, label: routeLabel(clean), occurred_at: now.toISOString() })
      if (buffer.length >= maxBatch) {
        void flush()
        return
      }
      if (!timerArmed) {
        timerArmed = true
        schedule(() => void flush(), flushMs)
      }
    },
    flush,
    size: () => buffer.length,
  }
}

async function sendPageViews(events: PageView[]): Promise<void> {
  await fetch(apiUrl('/me/activity/page-views'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events }),
    // Survive tab close / navigation away mid-flush.
    keepalive: true,
  })
}

/** Mount once at the router root. No-op in maket mode or without a token. */
export function usePageViewTracker(): void {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const accessToken = useAuthStore((s) => s.accessToken)
  const queueRef = useRef<PageViewQueue | null>(null)
  const enabled = USE_REAL_API && Boolean(accessToken)

  useEffect(() => {
    if (!enabled) return
    const queue = createPageViewQueue({ send: sendPageViews })
    queueRef.current = queue
    const onHide = () => {
      if (document.visibilityState === 'hidden') void queue.flush()
    }
    document.addEventListener('visibilitychange', onHide)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      void queue.flush()
      queueRef.current = null
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return
    queueRef.current?.push(pathname)
  }, [enabled, pathname])
}
